import { HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { HttpClientService } from '../../shared/services/http-client/http-client.service';
import { RelatorioFormato } from '../enum/relatorio-formato.enum';
import {
  CampanhaListQueryParams,
  CampanhaPlanoElegivelResponseDto,
  CampanhaRegraElegibilidadeResponseDto,
  CampanhaResponseDto,
  CreateCampanhaCommandDto,
  CreateCampanhaRegraElegibilidadeCommandDto,
  ImportarParticipantesResponseDto,
  PatchCampanhaCommandDto,
  UpdateCampanhaCommandDto,
  PerfilParticipanteDto,
  PerfilParticipanteQueryParams,
  PlanoResponseDto,
  QuestionarioListQueryParams,
  QuestionarioResponseDto,
  SincronizarCampanhaPlanoElegivelCommandDto,
  TipoCampanhaResponseDto,
} from '../api/models';

const CAMPANHA_BASE_PATH = '/v1/Campanha';
const PERFIL_PARTICIPANTE_BASE_PATH = '/v1/PerfilParticipante';
const QUESTIONARIO_BASE_PATH = '/v1/Questionario';
const PLANOS_BASE_PATH = '/v1/Planos';

@Injectable({
  providedIn: 'root',
})
export class CampanhaApiService {
  constructor(private readonly httpClient: HttpClientService) {}

  listCampanhas(params?: CampanhaListQueryParams): Observable<CampanhaResponseDto[]> {
    return this.httpClient.get<CampanhaResponseDto[]>(CAMPANHA_BASE_PATH, {
      params: this.toHttpParams(params),
    });
  }

  getCampanha(id: number | string): Observable<CampanhaResponseDto> {
    return this.httpClient.get<CampanhaResponseDto>(`${CAMPANHA_BASE_PATH}/${id}`);
  }

  listTiposCampanha(): Observable<TipoCampanhaResponseDto[]> {
    return this.httpClient.get<TipoCampanhaResponseDto[]>(`${CAMPANHA_BASE_PATH}/TiposCampanhas`);
  }

  createCampanha(command: CreateCampanhaCommandDto): Observable<CampanhaResponseDto> {
    return this.httpClient.post<CampanhaResponseDto>(CAMPANHA_BASE_PATH, command);
  }

  updateCampanha(
    id: number | string,
    command: UpdateCampanhaCommandDto,
  ): Observable<CampanhaResponseDto> {
    return this.httpClient.put<CampanhaResponseDto>(`${CAMPANHA_BASE_PATH}/${id}`, command);
  }

  patchCampanha(
    id: number | string,
    command: PatchCampanhaCommandDto,
  ): Observable<CampanhaResponseDto> {
    return this.httpClient.patch<CampanhaResponseDto>(`${CAMPANHA_BASE_PATH}/${id}`, command);
  }

  deleteCampanha(id: number | string): Observable<void> {
    return this.httpClient.delete<void>(`${CAMPANHA_BASE_PATH}/${id}`);
  }

  /** Adesões da campanha já no recorte pedido — o filtro roda no back. */
  listParticipantes(params: PerfilParticipanteQueryParams): Observable<PerfilParticipanteDto[]> {
    return this.httpClient.get<PerfilParticipanteDto[]>(PERFIL_PARTICIPANTE_BASE_PATH, {
      params: this.toPerfilParticipanteParams(params),
    });
  }

  /**
   * Planilha das adesões da campanha (`.xlsx`). Os filtros são opcionais: sem eles, o arquivo
   * traz a campanha inteira.
   */
  exportarParticipantes(params: PerfilParticipanteQueryParams): Observable<Blob> {
    return this.httpClient.getBlob(`${PERFIL_PARTICIPANTE_BASE_PATH}/Exportar`, {
      params: this.toPerfilParticipanteParams(params),
    });
  }

  /** Relatório da campanha no formato pedido — `1` PDF, `2` Excel, ambos no caminho. */
  exportarRelatorio(idCampanha: number | string, formato: RelatorioFormato): Observable<Blob> {
    return this.httpClient.getBlob(`${CAMPANHA_BASE_PATH}/${idCampanha}/Exportar/${formato}`);
  }

  listPlanos(numPlbnf?: number): Observable<PlanoResponseDto[]> {
    const params =
      numPlbnf != null ? new HttpParams().set('numPlbnf', String(numPlbnf)) : undefined;

    return this.httpClient.get<PlanoResponseDto[]>(PLANOS_BASE_PATH, { params });
  }

  listQuestionarios(
    params?: QuestionarioListQueryParams,
  ): Observable<QuestionarioResponseDto[]> {
    return this.httpClient.get<QuestionarioResponseDto[]>(QUESTIONARIO_BASE_PATH, {
      params: this.toQuestionarioHttpParams(params),
    });
  }

  listRegrasElegibilidade(
    idCampanha: number | string,
  ): Observable<CampanhaRegraElegibilidadeResponseDto[]> {
    return this.httpClient.get<CampanhaRegraElegibilidadeResponseDto[]>(
      `${CAMPANHA_BASE_PATH}/${idCampanha}/RegrasElegibilidade`,
    );
  }

  createRegraElegibilidade(
    idCampanha: number | string,
    body: CreateCampanhaRegraElegibilidadeCommandDto = { status: 'A' },
  ): Observable<CampanhaRegraElegibilidadeResponseDto> {
    const payload: CreateCampanhaRegraElegibilidadeCommandDto = {
      status: body.status ?? 'A',
    };

    return this.httpClient.post<CampanhaRegraElegibilidadeResponseDto>(
      `${CAMPANHA_BASE_PATH}/${idCampanha}/RegrasElegibilidade`,
      payload,
    );
  }

  listPlanosElegiveis(
    idCampanha: number | string,
    idRegra: number | string,
  ): Observable<CampanhaPlanoElegivelResponseDto[]> {
    return this.httpClient.get<CampanhaPlanoElegivelResponseDto[]>(
      `${CAMPANHA_BASE_PATH}/${idCampanha}/RegrasElegibilidade/${idRegra}/PlanosElegiveis`,
    );
  }

  syncPlanosElegiveis(
    idCampanha: number | string,
    idRegra: number | string,
    numsPlbnf: number[],
  ): Observable<CampanhaPlanoElegivelResponseDto[]> {
    const body: SincronizarCampanhaPlanoElegivelCommandDto = { planos: numsPlbnf };

    return this.httpClient.put<CampanhaPlanoElegivelResponseDto[]>(
      `${CAMPANHA_BASE_PATH}/${idCampanha}/RegrasElegibilidade/${idRegra}/PlanosElegiveis`,
      body,
    );
  }

  importarParticipantesElegiveis(
    idCampanha: number | string,
    arquivo: File,
  ): Observable<ImportarParticipantesResponseDto> {
    const formData = new FormData();
    formData.append('arquivo', arquivo);

    return this.httpClient.post<ImportarParticipantesResponseDto>(
      `${CAMPANHA_BASE_PATH}/${idCampanha}/ImportarParticipantes`,
      formData,
    );
  }

  private toQuestionarioHttpParams(
    params?: QuestionarioListQueryParams,
  ): HttpParams | undefined {
    if (!params) {
      return undefined;
    }

    let httpParams = new HttpParams();

    if (params.idQuestionario != null) {
      httpParams = httpParams.set('idQuestionario', String(params.idQuestionario));
    }

    if (params.nome) {
      httpParams = httpParams.set('nome', params.nome);
    }

    if (params.status) {
      httpParams = httpParams.set('status', params.status);
    }

    return httpParams.keys().length > 0 ? httpParams : undefined;
  }

  /** Filtro vazio fica fora da query — o back trata a ausência como "sem recorte". */
  private toPerfilParticipanteParams(params: PerfilParticipanteQueryParams): HttpParams {
    let httpParams = new HttpParams().set('idCampanha', String(params.idCampanha));

    if (params.nomeParticipante) {
      httpParams = httpParams.set('nomeParticipante', params.nomeParticipante);
    }

    if (params.status) {
      httpParams = httpParams.set('status', params.status);
    }

    if (params.dataConfirmacaoTermo) {
      httpParams = httpParams.set('dataConfirmacaoTermo', params.dataConfirmacaoTermo);
    }

    return httpParams;
  }

  private toHttpParams(params?: CampanhaListQueryParams): HttpParams | undefined {
    if (!params) {
      return undefined;
    }

    let httpParams = new HttpParams();

    if (params.idCampanha != null) {
      httpParams = httpParams.set('idCampanha', String(params.idCampanha));
    }

    if (params.nmCampanha) {
      httpParams = httpParams.set('nmCampanha', params.nmCampanha);
    }

    if (params.status) {
      httpParams = httpParams.set('status', params.status);
    }

    return httpParams.keys().length > 0 ? httpParams : undefined;
  }
}
